// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import _ from "lodash";
import styled from "styled-components";
import moment from 'moment';
import { Redirect } from 'react-router-dom';

import * as selectors from "../../modules/selectors";
import * as actions from "../../modules/actions";
import {
	isDraftComplete,
	isDraftActive,
	isDraftFuture,
	isDraftUnscheduled,
	getDefaultLeagueRoute
} from '../../helpers/league';

import {
	AdsContainer,
	Ad,
} from '../../components';

import OneColumnLayout from '../../components/OneColumnLayout';
import PageTitle from '../../components/PageTitle';
import JsonFetcher from '../../components/utils/JsonFetcher';
import RegenerateLeagues from '../../components/RegenerateLeagues';

import { above } from '../../assets/css/media';

import FutureLeague from './myLeagues/FutureLeague';
import LiveDraftLeague from './myLeagues/LiveDraftLeague';
import LiveLeague from './myLeagues/LiveLeague';
import UnscheduledLeague from './myLeagues/UnscheduledLeague';

export const StyledPageTitle = styled(PageTitle)`
	margin-bottom: 16px;
`;

const InnerSpacing = styled.div`
	margin-top: 10px;
	margin-bottom: 50px;

	${above.phone`
		margin-top: 30px;
	`}
`;

type League = Object;

type Props = {
	myList: typeof actions.leagueDraft.myList,
	requestServerAndLocalTimeDiff: typeof actions.leagueDraft.requestServerAndLocalTimeDiff,
	my_leagues: Array<League>,
	has_my_leagues: boolean,
	live_leagues: Array<League>,
	has_live_leagues: boolean,
	live_draft_leagues: Array<League>,
	has_live_draft_leagues: boolean,
	future_leagues: Array<League>,
	has_future_leagues: boolean,
	unscheduled_leagues: Array<League>,
	has_unscheduled_leagues: boolean,
	my_list_success: boolean,
	user: {
		id: number,
		avatar_version: number,
		firstname: string,
		lastname: string
	},
	actual_round: Object,
	active_or_last_complete_or_next_scheduled_round: Object,
	rounds_by_id: Array<Object>,
	coach_players_by_id: Object,
	has_assistant_coach: boolean,
	single_league: Object,
	server_time_diff: number,
	players: Object,
};

type State = {
	last_fetch: ?string
};

class LeaguesPage extends React.Component<Props, State> {
	state = {
		last_fetch: moment().format()
	};

	componentDidMount() {
		const { myList, requestServerAndLocalTimeDiff } = this.props;

		requestServerAndLocalTimeDiff();
		myList();
	}

	onTimerComplete = (date_string: string) => {
		const { myList } = this.props;

		this.setState(state => {
			if (moment(date_string).isAfter(state.last_fetch)) {
				myList();

				return { last_fetch: date_string };
			}

			return state;
		});
	};

	renderLiveLeagues = () => {
		const {
			live_leagues,
			has_live_leagues,
			live_draft_leagues,
			user,
			actual_round,
			active_or_last_complete_or_next_scheduled_round,
			rounds_by_id,
			coach_players_by_id,
			has_assistant_coach,
			players
		} = this.props;

		return (
			<React.Fragment>
				<StyledPageTitle>
					Your Live Leagues
				</StyledPageTitle>
				{has_live_leagues && <JsonFetcher fetch={['rounds', 'coach_players', 'players']}
					interval={60000} />}
				<ul>
					{live_draft_leagues.map(league => {
						const is_commissioner = league.commissioner === user.id;

						return (
							<li key={league.id}>
								<LiveDraftLeague
									league={league}
									is_commissioner={is_commissioner}
								/>
							</li>
						);
					})}
					{live_leagues.map(league => {
						const is_commissioner = league.commissioner === user.id;

						const start_round = league.start_round;
						const round = start_round > actual_round.id ?
							rounds_by_id[start_round] :
							actual_round;
						const fixture_round =
							start_round > active_or_last_complete_or_next_scheduled_round.id
								? rounds_by_id[start_round]
								: active_or_last_complete_or_next_scheduled_round;

						return (
							<li key={league.id}>
								<LiveLeague
									league={league}
									is_commissioner={is_commissioner}
									onTimerComplete={this.onTimerComplete}
									round={round}
									fixture_round={fixture_round}
									user={user}
									coach_players_by_id={coach_players_by_id}
									has_assistant_coach={has_assistant_coach}
									players={players}
								/>
							</li>
						);
					})}
				</ul>
			</React.Fragment>
		);
	};

	renderFutureLeagues = () => {
		const { future_leagues, user, server_time_diff } = this.props;

		return (
			<React.Fragment>
				<StyledPageTitle>
					Your Future Draft Leagues
				</StyledPageTitle>
				<ul>
					{future_leagues.map(league => {
						const is_commissioner = league.commissioner === user.id;
						return (
							<li key={league.id}>
								<FutureLeague
									server_time_diff={server_time_diff}
									league={league}
									is_commissioner={is_commissioner}
									onTimerComplete={this.onTimerComplete}
								/>
							</li>
						);
					})}
				</ul>
			</React.Fragment>
		);
	};

	renderUnscheduledLeagues = () => {
		const { unscheduled_leagues, user } = this.props;

		return (
			<React.Fragment>
				<StyledPageTitle>
					Your Unscheduled Leagues
				</StyledPageTitle>
				<ul>
					{unscheduled_leagues.map(league => {
						const is_commissioner = league.commissioner === user.id;
						return (
							<li key={league.id}>
								<UnscheduledLeague
									league={league}
									is_commissioner={is_commissioner}
								/>
							</li>
						);
					})}
				</ul>
			</React.Fragment>
		);
	};

	get shouldRedirectToJoin() {
		const {
			has_my_leagues,
			my_list_success,
		} = this.props;

		return (my_list_success && !has_my_leagues);
	}

	get shouldRenderLiveLeagues() {
		const {
			has_live_leagues,
			has_live_draft_leagues,
		} = this.props;

		return (has_live_leagues || has_live_draft_leagues);
	}

	render() {
		const {
			has_future_leagues,
			has_unscheduled_leagues,
			single_league
		} = this.props;

		if (single_league) {
			const league_default_link = getDefaultLeagueRoute(single_league);

			if (league_default_link) {
				return <Redirect to={league_default_link} />;
			}
		}

		if (this.shouldRedirectToJoin) {
			return <Redirect to='/leagues/join' />;
		}

		return (
			<React.Fragment>
				<AdsContainer>
					<Ad
						adUnit='myleagues'
						size='top'
					/>
				</AdsContainer>
				<OneColumnLayout>
					<InnerSpacing>
						{(this.shouldRenderLiveLeagues) ? this.renderLiveLeagues() : null}
						{has_future_leagues ? this.renderFutureLeagues() : null}
						{has_unscheduled_leagues ? this.renderUnscheduledLeagues() : null}
						<RegenerateLeagues large_heading />
					</InnerSpacing>
				</OneColumnLayout>
				<AdsContainer>
					<Ad
						adUnit='myleagues'
						size='bottom'
					/>
				</AdsContainer>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state, props) => {
	const my_leagues = state.leagues.my_list.ordered_ids.map(
		league_id => state.leagues.my_list.by_id[league_id]
	);
	const live_leagues = my_leagues.filter(isDraftComplete);
	const live_draft_leagues = my_leagues.filter(isDraftActive);
	const future_leagues = my_leagues.filter(isDraftFuture);
	const unscheduled_leagues = my_leagues.filter(isDraftUnscheduled);

	const single_league = my_leagues.length === 1 ? my_leagues[0] : null;


	return {
		server_time_diff: state.leagues.server_time_diff,
		user: state.user.data,
		user_id: state.user.data.id,
		user_avatar_version: state.user.data.avatar_version,
		my_leagues,
		has_my_leagues: _.isEmpty(my_leagues) === false,
		live_leagues,
		has_live_leagues: _.isEmpty(live_leagues) === false,
		live_draft_leagues,
		my_list_success: state.leagues.my_list.success,
		has_live_draft_leagues: _.isEmpty(live_draft_leagues) === false,
		future_leagues,
		has_future_leagues: _.isEmpty(future_leagues) === false,
		unscheduled_leagues,
		has_unscheduled_leagues: _.isEmpty(unscheduled_leagues) === false,
		actual_round: selectors.rounds.getActualRound(state),
		active_or_last_complete_or_next_scheduled_round:
			selectors.rounds.getActiveOrLastCompleteOrNextScheduledRound(state),
		rounds_by_id: selectors.rounds.getRoundsById(state),
		coach_players_by_id: state.players.coach_by_id,
		single_league,
		has_assistant_coach: Boolean(selectors.getUser(state).assistant_coach),
		players: selectors.players.getExtendedPlayersById(state, props),
	};
};

const mapDispatchToProps = {
	myList: actions.leagueDraft.myList,
	requestServerAndLocalTimeDiff: actions.leagueDraft.requestServerAndLocalTimeDiff,
};

export const Leagues = connect(
	mapStateToProps,
	mapDispatchToProps
)(LeaguesPage);

export default Leagues;


// WEBPACK FOOTER //
// ./src/pages/leagues/MyLeagues.js